import { useCallback, useEffect, useState } from 'react';
import { api } from './auth';
import { upsertById } from './trade';

// Everything the Sales page's trade tabs work from: businesses, invoices, the
// price list and the invoice settings. Loaded once with the page, then kept
// current from what each save sends back rather than by reloading.
//
// status: 'loading' | 'ready' | 'missing' (the server has no /trade routes
// yet, i.e. garageFarmServer hasn't been deployed) | 'error'
export function useTradeData() {
  const [data, setData] = useState({ accounts: [], invoices: [], priceList: null, settings: null });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [accounts, invoices, priceList, settings] = await Promise.all(
        ['/trade/accounts', '/trade/invoices', '/trade/price-list', '/trade/invoice-settings']
          .map(url => api.get(url).then(r => r.data)),
      );
      setData({ accounts, invoices, priceList, settings });
      setStatus('ready');
    } catch (e) {
      if (e.response?.status === 404) return setStatus('missing');
      setError(e.response?.data?.error || e.message);
      setStatus('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const putAccount  = useCallback(a  => setData(d => ({ ...d, accounts: upsertById(d.accounts, a) })), []);
  const putInvoice  = useCallback(i  => setData(d => ({ ...d, invoices: upsertById(d.invoices, i) })), []);
  const dropAccount = useCallback(id => setData(d => ({ ...d, accounts: d.accounts.filter(a => a._id !== id) })), []);
  const dropInvoice = useCallback(id => setData(d => ({ ...d, invoices: d.invoices.filter(i => i._id !== id) })), []);
  const setPriceList = useCallback(priceList => setData(d => ({ ...d, priceList })), []);
  const setSettings  = useCallback(settings  => setData(d => ({ ...d, settings })), []);

  // Invoice saves send back { invoice, account }: apply whichever came back.
  const apply = useCallback(({ invoice, account } = {}) => {
    if (invoice) putInvoice(invoice);
    if (account) putAccount(account);
  }, [putInvoice, putAccount]);

  return {
    ...data, status, error, reload: load,
    putAccount, putInvoice, dropAccount, dropInvoice, setPriceList, setSettings, apply,
  };
}
